import React, { useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";

const ROLE_ORDER = ["booker", "main_guest", "accompanying", "child"];
const ROLE_LABELS = {
  booker: "Booker",
  main_guest: "Main guest",
  accompanying: "Accompanying guest",
  child: "Child",
};

function formatDate(dateValue) {
  if (!dateValue) return "Not applied yet";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "Invalid date";
  return parsed.toLocaleString();
}

function normalizeEmailList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item || "").trim()).filter(Boolean);
}

export default function OnboardingReservationSettingsAdmin() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useState(null);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applyingBusinessUnitId, setApplyingBusinessUnitId] = useState(null);
  const [feedback, setFeedback] = useState("");

  const draft = selectedDraft?.draft || null;
  const totalRequiredFields = useMemo(
    () =>
      Object.values(draft?.required_customer_fields_reservation || {}).reduce(
        (total, roleFields) => total + (Array.isArray(roleFields) ? roleFields.length : 0),
        0
      ),
    [draft?.required_customer_fields_reservation]
  );
  const notificationRecipients = useMemo(
    () => normalizeEmailList(draft?.external_reservation_notification_emails),
    [draft?.external_reservation_notification_emails]
  );
  const confirmationLanguages = useMemo(
    () =>
      Array.isArray(draft?.reservation_confirmation_email_languages)
        ? draft.reservation_confirmation_email_languages.map((languageKey) => String(languageKey || "").toUpperCase())
        : [],
    [draft?.reservation_confirmation_email_languages]
  );

  const loadDrafts = async () => {
    setLoading(true);
    setFeedback("");

    try {
      const response = await axios.get("/api/onboarding/admin/reservation-settings/drafts");
      setDrafts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("[Admin][Onboarding][ReservationSettings] Error loading drafts:", error);
      setFeedback("Could not load onboarding reservation settings drafts.");
    } finally {
      setLoading(false);
    }
  };

  const loadDraftDetail = async (businessUnitId) => {
    setSelectedBusinessUnitId(businessUnitId);
    setDetailLoading(true);
    setFeedback("");

    try {
      const response = await axios.get(`/api/onboarding/admin/reservation-settings/${businessUnitId}`);
      setSelectedDraft(response.data);
    } catch (error) {
      console.error("[Admin][Onboarding][ReservationSettings] Error loading draft detail:", error);
      setSelectedDraft(null);
      setFeedback("Could not load the selected reservation settings draft.");
    } finally {
      setDetailLoading(false);
    }
  };

  const applyDraft = async (businessUnitId, businessUnitName) => {
    const confirmed = window.confirm(
      `Apply the onboarding reservation settings draft for ${businessUnitName}? This will update hotel_settings for reservation operations.`
    );

    if (!confirmed) return;

    setApplyingBusinessUnitId(businessUnitId);
    setFeedback("");

    try {
      await axios.post(`/api/onboarding/admin/reservation-settings/${businessUnitId}/apply`);
      setFeedback(`Reservation settings draft applied for ${businessUnitName}.`);
      await loadDrafts();
      await loadDraftDetail(businessUnitId);
    } catch (error) {
      console.error("[Admin][Onboarding][ReservationSettings] Error applying draft:", error);
      setFeedback(error?.response?.data?.error || "Could not apply the selected reservation settings draft.");
    } finally {
      setApplyingBusinessUnitId(null);
    }
  };

  useEffect(() => {
    loadDrafts();
  }, []);

  return (
    <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
      <div
        style={{
          borderRadius: "22px",
          padding: "22px",
          border: "1px solid rgba(255,255,255,0.08)",
          background:
            "radial-gradient(780px 240px at 0% 0%, rgba(92,191,255,0.12), transparent 60%), rgba(10,15,28,0.9)",
          boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.12em", color: "#8ea0bc" }}>
          Admin apply area
        </div>
        <h2 style={{ color: "#fff", margin: "8px 0 10px" }}>Onboarding Reservation Settings</h2>
        <p style={{ color: "#b9c3d7", maxWidth: "940px", margin: 0, lineHeight: 1.6 }}>
          Reservation rules saved in onboarding remain staged until an admin reviews them here and applies them to{" "}
          <strong>`hotel_settings`</strong>. Theme and language are the only onboarding choices that apply directly.
        </p>
      </div>

      {feedback ? (
        <div
          style={{
            borderRadius: "14px",
            padding: "12px 14px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: feedback.toLowerCase().includes("could not") ? "#542929" : "#244428",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          {feedback}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)", gap: "18px" }}>
        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <div>
              <h3 style={{ color: "#fff", margin: 0 }}>Drafts</h3>
              <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                Business units with staged reservation settings.
              </p>
            </div>
            <button
              type="button"
              onClick={loadDrafts}
              disabled={loading}
              style={{
                borderRadius: "12px",
                border: "none",
                padding: "10px 14px",
                background: "#5cbfff",
                color: "#08111e",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {loading ? (
              <div style={{ color: "#9db0cf" }}>Loading reservation settings drafts...</div>
            ) : drafts.length === 0 ? (
              <div style={{ color: "#9db0cf" }}>No reservation settings drafts available yet.</div>
            ) : (
              drafts.map((row) => {
                const isSelected = Number(selectedBusinessUnitId) === Number(row.business_unit_id);
                return (
                  <article
                    key={row.business_unit_id}
                    style={{
                      borderRadius: "18px",
                      padding: "16px",
                      border: `1px solid ${isSelected ? "rgba(92,191,255,0.45)" : "rgba(255,255,255,0.08)"}`,
                      background: isSelected ? "rgba(92,191,255,0.08)" : "rgba(255,255,255,0.03)",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
                      <div>
                        <h4 style={{ color: "#fff", margin: 0 }}>{row.business_unit_name}</h4>
                        <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                          {row.checkin_time?.slice?.(0, 5) || "--:--"} / {row.checkout_time?.slice?.(0, 5) || "--:--"}
                        </p>
                      </div>
                      <div style={{ color: "#e7edf8", fontWeight: 700 }}>
                        {row.notifications_enabled ? `${row.notification_recipients} emails` : "Notifications off"}
                      </div>
                    </div>

                    <div style={{ marginTop: "12px", color: "#9db0cf", fontSize: "0.9rem", lineHeight: 1.5 }}>
                      <div>Last save: {formatDate(row.updated_at)}</div>
                      <div>Last apply: {formatDate(row.applied_at)}</div>
                    </div>

                    <div style={{ marginTop: "14px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => loadDraftDetail(row.business_unit_id)}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#1b2c43",
                          color: "#fff",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Preview
                      </button>
                      <button
                        type="button"
                        onClick={() => applyDraft(row.business_unit_id, row.business_unit_name)}
                        disabled={applyingBusinessUnitId === row.business_unit_id}
                        style={{
                          borderRadius: "12px",
                          border: "none",
                          padding: "10px 14px",
                          background: "#ff975c",
                          color: "#08111e",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        {applyingBusinessUnitId === row.business_unit_id ? "Applying..." : "Apply to live"}
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section
          style={{
            borderRadius: "20px",
            padding: "18px",
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(10,15,28,0.9)",
            boxShadow: "0 24px 60px rgba(0,0,0,0.18)",
          }}
        >
          {!selectedBusinessUnitId ? (
            <div style={{ color: "#9db0cf" }}>Select a draft to preview its staged reservation settings.</div>
          ) : detailLoading ? (
            <div style={{ color: "#9db0cf" }}>Loading draft detail...</div>
          ) : !selectedDraft ? (
            <div style={{ color: "#9db0cf" }}>No draft detail loaded.</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
                <div>
                  <h3 style={{ color: "#fff", margin: 0 }}>{selectedDraft.session?.business_unit_name}</h3>
                  <p style={{ color: "#9db0cf", margin: "6px 0 0" }}>
                    Last save: {formatDate(selectedDraft.session?.updated_at)}. Last apply:{" "}
                    {formatDate(selectedDraft.session?.applied_at)}.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    applyDraft(selectedDraft.session.business_unit_id, selectedDraft.session.business_unit_name)
                  }
                  disabled={applyingBusinessUnitId === selectedDraft.session?.business_unit_id}
                  style={{
                    borderRadius: "12px",
                    border: "none",
                    padding: "12px 16px",
                    background: "#5cbfff",
                    color: "#08111e",
                    fontWeight: 800,
                    cursor: "pointer",
                    opacity: applyingBusinessUnitId === selectedDraft.session?.business_unit_id ? 0.7 : 1,
                  }}
                >
                  {applyingBusinessUnitId === selectedDraft.session?.business_unit_id ? "Applying..." : "Apply draft"}
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px", marginBottom: "16px" }}>
                {[
                  ["Check-in", draft?.checkin_time?.slice?.(0, 5) || "--:--"],
                  ["Check-out", draft?.checkout_time?.slice?.(0, 5) || "--:--"],
                  ["Required fields", totalRequiredFields],
                  [
                    "External notifications",
                    draft?.external_reservation_notifications_enabled
                      ? `${notificationRecipients.length} recipients`
                      : "Disabled",
                  ],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: "rgba(255,255,255,0.03)",
                      padding: "14px",
                    }}
                  >
                    <div style={{ color: "#8ea0bc", fontSize: "0.82rem" }}>{label}</div>
                    <div style={{ color: "#fff", fontSize: "1.1rem", fontWeight: 800, marginTop: "6px" }}>{value}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gap: "14px" }}>
                <section
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.03)",
                    padding: "14px",
                  }}
                >
                  <h4 style={{ color: "#fff", margin: "0 0 10px" }}>Age rules</h4>
                  <div style={{ color: "#c7d2e7", lineHeight: 1.7 }}>
                    <div>Baby max age: {draft?.max_age_baby ?? "-"}</div>
                    <div>Child max age: {draft?.max_age_child ?? "-"}</div>
                    <div>Main guest minimum age: {draft?.min_age_main_guest ?? "-"}</div>
                    <div>Booker minimum age: {draft?.min_age_booker ?? "-"}</div>
                    <div>
                      Group reservation default status: {draft?.group_reservations_default_status || "-"}
                    </div>
                  </div>
                </section>

                <section
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.03)",
                    padding: "14px",
                  }}
                >
                  <h4 style={{ color: "#fff", margin: "0 0 10px" }}>Required customer fields</h4>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
                    {ROLE_ORDER.map((roleKey) => {
                      const fields = Array.isArray(draft?.required_customer_fields_reservation?.[roleKey])
                        ? draft.required_customer_fields_reservation[roleKey]
                        : [];
                      return (
                        <div
                          key={roleKey}
                          style={{
                            borderRadius: "12px",
                            border: "1px solid rgba(255,255,255,0.08)",
                            background: "rgba(10,15,28,0.42)",
                            padding: "12px",
                          }}
                        >
                          <div style={{ color: "#fff", fontWeight: 700 }}>{ROLE_LABELS[roleKey] || roleKey}</div>
                          <div style={{ color: "#9db0cf", marginTop: "6px", lineHeight: 1.6 }}>
                            {fields.length ? fields.join(", ") : "No extra required fields"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section
                  style={{
                    borderRadius: "14px",
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.03)",
                    padding: "14px",
                  }}
                >
                  <h4 style={{ color: "#fff", margin: "0 0 10px" }}>Confirmation and hotel notifications</h4>
                  <div style={{ color: "#c7d2e7", lineHeight: 1.7 }}>
                    <div>
                      Required roles:{" "}
                      {Array.isArray(draft?.required_customer_roles_reservation) &&
                      draft.required_customer_roles_reservation.length
                        ? draft.required_customer_roles_reservation
                            .map((roleKey) => ROLE_LABELS[roleKey] || roleKey)
                            .join(", ")
                        : "None"}
                    </div>
                    <div>
                      Confirmation email languages: {confirmationLanguages.length ? confirmationLanguages.join(", ") : "None"}
                    </div>
                    <div>
                      Hotel notifications: {draft?.external_reservation_notifications_enabled ? "Enabled" : "Disabled"}
                    </div>
                    <div>
                      Recipients: {notificationRecipients.length ? notificationRecipients.join(", ") : "No recipients configured"}
                    </div>
                  </div>
                </section>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
